import {
  type FC, useCallback,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { getCheckIsBoundaryGroupFromProvider } from '~/store/boundaryGroups/boundaryGroups.helpers';
import { useGlobalBoundaryGroups } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { DemographicsDataProviders } from '~/store/demographics/demographics.types';
import { useDemographicsListPremiumIsLoadingSelector } from '~/store/demographics/premiumDemographics/demographicsListPremium.selectors';
import { useIsDataLayerToolUpdatingSelector } from '~/store/frontendState/mapTools/dataLayers/dataLayers.selectors';
import { useDataLayersTemplates } from '~/store/frontendState/mapTools/dataLayers/useDataLayersTemplates';
import { mapSettingsDataLayersUpdateBoundaryGroup } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  useMapSettingsActiveDataLayerSelector, useMapSettingsBoundaryGroupsPerDataLayersSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import { useToolCollapseState } from '../useToolCollapseState';
import { DataLayersToolComponent } from './dataLayersTool.component';

export const DataLayersToolContainer: FC = () => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const { isToolCollapsed } = useToolCollapseState();
  const isEditModeDisabled = useIsMapEditDisabled();
  const globalBoundaryGroups = useGlobalBoundaryGroups();
  const activeDataLayer = useMapSettingsActiveDataLayerSelector();
  const boundaryGroupPerDataLayer = useMapSettingsBoundaryGroupsPerDataLayersSelector();
  const isPremiumDemographicsLoading = useDemographicsListPremiumIsLoadingSelector();
  const isDataLayerToolUpdating = useIsDataLayerToolUpdatingSelector();
  const { templates, fetchDataLayerTemplates, createNewDataLayerFromTemplate } = useDataLayersTemplates();

  const onChangeBoundaryGroup = useCallback((boundaryGroupId: BoundaryGroupId) => {
    if (!activeDataLayer) {
      return;
    }
    dispatch(mapSettingsDataLayersUpdateBoundaryGroup(activeDataLayer.id, boundaryGroupId));
  }, [activeDataLayer, dispatch]);

  const premiumDataBoundaryGroups = useMemo(() => {
    const checkIsAgs = getCheckIsBoundaryGroupFromProvider(DemographicsDataProviders.AGS);
    return globalBoundaryGroups.filter(bg => checkIsAgs(bg));
  }, [globalBoundaryGroups]);

  const allowedBTGs = useMemo(() => (
    premiumDataBoundaryGroups
      .filter(bg => bg.region === activeDataLayer?.data.region)
      .map(bg => bg.id)
  ), [activeDataLayer?.data.region, premiumDataBoundaryGroups]);

  const selectedBoundaryGroup = activeDataLayer && boundaryGroupPerDataLayer[activeDataLayer.id]
    ? (boundaryGroupPerDataLayer[activeDataLayer.id] ?? null)
    : null;

  useOnMount(() => {
    if (!clientId) {
      return;
    }
    fetchDataLayerTemplates();
  });

  return (
    <DataLayersToolComponent
      changeBoundaryGroupProps={{
        allBoundaryGroups: premiumDataBoundaryGroups,
        allowedBTGs,
        onChange: onChangeBoundaryGroup,
        initialBoundaryGroup: selectedBoundaryGroup,
      }}
      hasActiveDataLayer={!!activeDataLayer}
      isLegendMode={isToolCollapsed || isEditModeDisabled}
      isLoading={isPremiumDemographicsLoading || isDataLayerToolUpdating}
      boundaryGroups={premiumDataBoundaryGroups}
      savedLayerGroups={templates}
      onCreateFromTemplate={createNewDataLayerFromTemplate}
    />
  );
};
